import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowRight, BellRing, Save } from 'lucide-react';
import { useSystem } from '@ohif/core';
import { ICON_STROKE_WIDTH } from '../../constants';
import { API_ENDPOINTS } from '../../config/api.config';
import {
  MAX_PEER_REVIEW_LEVELS,
  MAX_PEER_REVIEW_REMINDER_MINUTES,
  MAX_PEER_REVIEW_TIMER_MINUTES,
  MIN_PEER_REVIEW_REMINDER_MINUTES,
  MIN_PEER_REVIEW_TIMER_MINUTES,
} from '../../constants/peerReviewChain.constants';
import {
  buildDefaultPeerReviewChain,
  getPeerReviewChain,
  savePeerReviewChain,
} from '../../services/peerReviewChainService';
import type { User, UserListResponse } from '../../types/user.types';
import type {
  PeerReviewChainFormState,
  PeerReviewLevelFormState,
  PeerReviewerOption,
} from '../../types/peerReviewChain.types';
import { apiGet } from '../../utils/apiClient';
import PeerReviewLevelBox from './PeerReviewLevelBox';

interface PeerReviewSectionProps {
  facilityId: number;
}

function formatReviewerSubtitle(user: {
  designation?: string | null;
  specialty?: string[] | string | null;
  reporting_modality_access?: string[] | null;
}): string {
  if (user.designation?.trim()) {
    return user.designation.trim();
  }
  if (Array.isArray(user.specialty) && user.specialty.length) {
    return user.specialty
      .map(item => String(item).trim())
      .filter(Boolean)
      .join(', ');
  }
  if (typeof user.specialty === 'string' && user.specialty.trim()) {
    return user.specialty.trim();
  }
  const modalities = (user.reporting_modality_access || [])
    .map(entry => String(entry).trim())
    .filter(Boolean);
  return modalities.join(', ');
}

function parseUsersResponse(payload: UserListResponse | User[] | undefined): User[] {
  if (!payload) {
    return [];
  }
  if (Array.isArray(payload)) {
    return payload;
  }
  if (payload.success && Array.isArray(payload.data)) {
    return payload.data;
  }
  return [];
}

async function loadRadiologists(facilityId: number): Promise<{
  success: boolean;
  data: PeerReviewerOption[];
  error?: string;
}> {
  try {
    const response = await apiGet<UserListResponse | User[]>(
      API_ENDPOINTS.RADIOLOGISTS.GET({
        page: 1,
        limit: 100,
        role: 'Radiologist',
        status: 'Active',
        facility_id: facilityId,
      })
    );
    const users = parseUsersResponse(response.data);
    return {
      success: true,
      data: users
        .filter(user => user.id != null)
        .map(user => ({
          id: Number(user.id),
          fullName: user.full_name,
          subtitle: formatReviewerSubtitle(user),
        })),
    };
  } catch (error) {
    return {
      success: false,
      data: [],
      error: error instanceof Error ? error.message : 'Failed to load radiologists',
    };
  }
}

function parseReminderInput(value: string): number {
  const digits = value.replaceAll(/\D/g, '');
  if (!digits) {
    return 0;
  }
  return Math.min(Number(digits), MAX_PEER_REVIEW_REMINDER_MINUTES);
}

function formatChainPath(
  levels: PeerReviewLevelFormState[],
  reviewersById: Map<number, PeerReviewerOption>
): string {
  const names = levels
    .filter(
      (level): level is PeerReviewLevelFormState & { reviewerId: number } =>
        level.reviewerId != null
    )
    .map(level => reviewersById.get(level.reviewerId)?.fullName)
    .filter(Boolean);
  return names.length ? names.join(' → ') : '—';
}

function getConfiguredLevels(levels: PeerReviewLevelFormState[]): PeerReviewLevelFormState[] {
  return levels.filter(level => level.reviewerId != null);
}

function validateChain(chain: PeerReviewChainFormState): string | null {
  if (
    !chain.reminderIntervalMinutes ||
    chain.reminderIntervalMinutes < MIN_PEER_REVIEW_REMINDER_MINUTES ||
    chain.reminderIntervalMinutes > MAX_PEER_REVIEW_REMINDER_MINUTES
  ) {
    return `Reminder interval must be between ${MIN_PEER_REVIEW_REMINDER_MINUTES} and ${MAX_PEER_REVIEW_REMINDER_MINUTES} minutes.`;
  }

  const configured = getConfiguredLevels(chain.levels);
  if (!configured.length) {
    return 'Set at least one reviewer in the peer review chain.';
  }

  for (let i = 0; i < configured.length; i += 1) {
    const expectedLevel = i + 1;
    if (configured[i].levelNumber !== expectedLevel) {
      return 'Fill reviewer levels in order without gaps.';
    }
    if (
      !configured[i].timerMinutes ||
      configured[i].timerMinutes < MIN_PEER_REVIEW_TIMER_MINUTES ||
      configured[i].timerMinutes > MAX_PEER_REVIEW_TIMER_MINUTES
    ) {
      return `Every set reviewer must have a review timer between ${MIN_PEER_REVIEW_TIMER_MINUTES} and ${MAX_PEER_REVIEW_TIMER_MINUTES} minutes.`;
    }
  }

  const ids = configured.map(level => level.reviewerId);
  if (new Set(ids).size !== ids.length) {
    return 'Each reviewer can appear only once in the chain.';
  }

  return null;
}

export default function PeerReviewSection({ facilityId }: Readonly<PeerReviewSectionProps>) {
  const { servicesManager } = useSystem();
  const { uiNotificationService } = servicesManager.services;

  const [chain, setChain] = useState<PeerReviewChainFormState>(buildDefaultPeerReviewChain);
  const [pathFromApi, setPathFromApi] = useState<string | null>(null);
  const [reviewers, setReviewers] = useState<PeerReviewerOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showValidation, setShowValidation] = useState(false);

  const reviewersById = useMemo(
    () => new Map(reviewers.map(reviewer => [reviewer.id, reviewer])),
    [reviewers]
  );

  const pathSummary = useMemo(() => {
    const localPath = formatChainPath(chain.levels, reviewersById);
    if (localPath !== '—') {
      return localPath;
    }
    return pathFromApi || '—';
  }, [chain.levels, reviewersById, pathFromApi]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadError(null);

    const [chainRes, usersRes] = await Promise.all([
      getPeerReviewChain(facilityId),
      loadRadiologists(facilityId),
    ]);

    setReviewers(usersRes.data);
    setPathFromApi(chainRes.matrix?.path ?? null);

    const activeIds = new Set(usersRes.data.map(option => option.id));
    const nextChain = chainRes.data;
    setChain({
      ...nextChain,
      levels: nextChain.levels.map(level => ({
        ...level,
        reviewerId:
          level.reviewerId != null && activeIds.has(level.reviewerId) ? level.reviewerId : null,
        timerMinutes:
          level.reviewerId != null && activeIds.has(level.reviewerId) ? level.timerMinutes : 0,
      })),
    });

    if (!usersRes.success) {
      setLoadError(usersRes.error || 'Failed to load radiologists');
    } else if (!chainRes.success) {
      setLoadError(chainRes.error || 'Failed to load peer review matrix');
    }

    setLoading(false);
  }, [facilityId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const updateLevel = (clientId: string, patch: Partial<PeerReviewLevelFormState>) => {
    setChain(prev => {
      let levels = prev.levels.map(level =>
        level.clientId === clientId ? { ...level, ...patch } : level
      );

      if (patch.reviewerId != null) {
        levels = levels.map(level =>
          level.clientId !== clientId && level.reviewerId === patch.reviewerId
            ? { ...level, reviewerId: null, timerMinutes: 0 }
            : level
        );
      }

      return { ...prev, levels };
    });
    setPathFromApi(null);
  };

  const getReservedReviewerIds = (currentClientId: string) =>
    new Set(
      chain.levels
        .filter(
          (level): level is PeerReviewLevelFormState & { reviewerId: number } =>
            level.clientId !== currentClientId && level.reviewerId != null
        )
        .map(level => level.reviewerId)
    );

  const handleSave = async () => {
    const validationError = validateChain(chain);
    setShowValidation(true);
    if (validationError) {
      uiNotificationService?.show({
        title: 'Validation failed',
        message: validationError,
        type: 'error',
        duration: 4000,
      });
      return;
    }

    setSaving(true);
    const result = await savePeerReviewChain(facilityId, chain);
    setSaving(false);

    if (!result.success || !result.data) {
      uiNotificationService?.show({
        title: 'Save failed',
        message: result.error || 'Unable to save peer review matrix',
        type: 'error',
        duration: 5000,
      });
      return;
    }

    setChain(result.data);
    setPathFromApi(result.matrix?.path ?? null);
    setShowValidation(false);
    uiNotificationService?.show({
      title: 'Peer review matrix saved',
      message: '',
      type: 'success',
      duration: 3000,
    });
  };

  return (
    <div className="ris-config-tab-panel">
      <section className="esc-step-card">
        <article className="esc-matrix-chain-card">
          <header className="esc-matrix-chain-header">
            <div className="esc-matrix-chain-heading">
              <div>
                <div className="esc-matrix-chain-title-row">
                  <h3 className="esc-matrix-chain-title">Peer review chain</h3>
                </div>
                <p className="esc-matrix-chain-path">Path: {pathSummary}</p>
              </div>
            </div>

            <div className="esc-matrix-chain-settings">
              <label className="esc-matrix-reminder">
                <BellRing
                  size={14}
                  strokeWidth={ICON_STROKE_WIDTH}
                  className="esc-matrix-reminder-icon"
                  aria-hidden
                />
                <span className="esc-matrix-reminder-label">Remind every</span>
                <input
                  type="text"
                  inputMode="numeric"
                  className="esc-matrix-reminder-input"
                  value={chain.reminderIntervalMinutes ? String(chain.reminderIntervalMinutes) : ''}
                  disabled={loading || saving}
                  onChange={e => {
                    setPathFromApi(null);
                    setChain(prev => ({
                      ...prev,
                      reminderIntervalMinutes: parseReminderInput(e.target.value),
                    }));
                  }}
                />
                <span className="esc-matrix-reminder-suffix">min</span>
              </label>
            </div>
          </header>

          {loadError && (
            <p
              className="esc-error-banner"
              role="alert"
            >
              {loadError}
            </p>
          )}

          {loading ? (
            <p className="esc-placeholder-text">Loading peer review chain…</p>
          ) : (
            <div className="esc-matrix-flow">
              {chain.levels.slice(0, MAX_PEER_REVIEW_LEVELS).map((level, index) => (
                <React.Fragment key={level.clientId}>
                  <PeerReviewLevelBox
                    level={level}
                    reviewers={reviewers}
                    reservedReviewerIds={getReservedReviewerIds(level.clientId)}
                    disabled={saving}
                    showValidation={showValidation}
                    onChange={patch => updateLevel(level.clientId, patch)}
                  />
                  {index < chain.levels.length - 1 && (
                    <div
                      className="esc-matrix-arrow"
                      aria-hidden="true"
                    >
                      <ArrowRight
                        size={20}
                        strokeWidth={ICON_STROKE_WIDTH}
                      />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </article>

        <div className="esc-matrix-save-row">
          <button
            type="button"
            className="esc-btn esc-btn--primary"
            disabled={loading || saving}
            onClick={handleSave}
          >
            <Save
              size={16}
              strokeWidth={ICON_STROKE_WIDTH}
            />
            {saving ? 'Saving…' : 'Save matrix'}
          </button>
        </div>
      </section>
    </div>
  );
}
